import { fitRect } from './fit-rect';
import { isPositiveSize } from './geometry-guards';
import { PersonBbox } from '../interfaces/image-album.interface';
import {
  calculateFaceCenteredPosition,
  calculatePersonCenteredPosition,
  FaceWithRelativeBbox,
  ImageRect,
} from './face-centering';

interface ContainerSize {
  readonly width: number;
  readonly height: number;
}

export interface VignettePlacement {
  readonly zoom: number;
  readonly focusX: number;
  readonly focusY: number;
}

export interface VignetteImageLike {
  readonly width: number;
  readonly height: number;
  readonly faces?: readonly unknown[];
  readonly personBboxes?: readonly PersonBbox[];
  readonly placement?: VignettePlacement;
}

const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

function coverCropPlacement(container: ContainerSize, imgW: number, imgH: number): ImageRect {
  return fitRect(imgW, imgH, container, { mode: 'cover', round: false });
}

export function toVignettePlacement(
  container: ContainerSize,
  imgW: number,
  imgH: number,
  rect: { left: number; top: number; width: number; height: number },
): VignettePlacement {
  const base = coverCropPlacement(container, imgW, imgH);
  const zoom = base.width > 0 ? rect.width / base.width : 1;
  const focusX = rect.width > 0 ? (container.width / 2 - rect.left) / rect.width : 0.5;
  const focusY = rect.height > 0 ? (container.height / 2 - rect.top) / rect.height : 0.5;
  return { zoom, focusX, focusY };
}

export function computeVignetteImagePlacement(container: ContainerSize, image: VignetteImageLike): ImageRect {
  if (!isPositiveSize(image.width, image.height)) {
    return { left: 0, top: 0, width: container.width, height: container.height };
  }

  const cover = coverCropPlacement(container, image.width, image.height);

  if (image.placement) {
    const { zoom, focusX, focusY } = image.placement;
    const width = cover.width * zoom;
    const height = cover.height * zoom;
    const left = clamp(container.width / 2 - focusX * width, container.width - width, 0);
    const top = clamp(container.height / 2 - focusY * height, container.height - height, 0);
    return { left, top, width, height };
  }

  const faces = (image.faces ?? []) as FaceWithRelativeBbox[];
  if (!faces.length) {
    const personBboxes = (image.personBboxes ?? []) as PersonBbox[];
    if (personBboxes.length) {
      const centering = calculatePersonCenteredPosition(
        personBboxes,
        { width: container.width, height: container.height },
        cover,
      );
      if (centering.shouldAdjust) {
        return { ...cover, left: cover.left + centering.offsetX, top: cover.top + centering.offsetY };
      }
    }

    return cover;
  }

  const centering = calculateFaceCenteredPosition(
    faces,
    image.width,
    image.height,
    { width: container.width, height: container.height },
    cover,
    1,
    image.personBboxes as PersonBbox[] | undefined,
    undefined,
  );

  if (!centering.shouldAdjust) return cover;
  return { ...cover, left: cover.left + centering.offsetX, top: cover.top + centering.offsetY };
}
